import React from 'react';
import { FastField, FieldArray } from 'formik';
import { Plus, Trash2, X } from 'lucide-react';

import { ErrorField } from '@/components/error-field';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

import { useFormikError } from '@/lib/error';

import { Field } from './ui/field';

type KeyValueItem = {
  uid?: string;
  key?: string;
  value?: string;
};

type KeyValueFieldArrayProps = {
  name: string;
  title: string;
  description: string;
  items: KeyValueItem[];
  appearance?: 'card' | 'compact';
  emptyMessage?: string;
  addLabel?: string;
};

type FormikFieldRenderProps = {
  field: {
    name: string;
    value: string;
    onChange: React.ChangeEventHandler<HTMLInputElement>;
    onBlur: React.FocusEventHandler<HTMLInputElement>;
  };
};

export const KeyValueFieldArray = React.memo(
  ({
    name,
    title,
    description,
    items,
    appearance = 'card',
    emptyMessage,
    addLabel = appearance === 'card' ? 'New key value pair' : 'Add',
  }: KeyValueFieldArrayProps) => {
    const isCard = appearance === 'card';
    const resolvedEmptyMessage = emptyMessage ?? `No ${title.toLowerCase()} configured.`;

    const hasError = useFormikError();

    const content = (
      <FieldArray
        name={name}
        render={arrayHelpers => (
          <div className="space-y-2 pt-4">
            {!isCard && (
              <div className="flex items-center justify-between gap-3">
                <div>
                  <Label className="text-sm font-medium">{title}</Label>
                  <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>
                </div>

                <Button
                  variant="ghost"
                  size="sm"
                  type="button"
                  className="h-7 px-2 text-xs"
                  onClick={() => arrayHelpers.push({ key: '', value: '' })}
                >
                  <Plus className="mr-1 h-3 w-3" />
                  {addLabel}
                </Button>
              </div>
            )}

            {items.length === 0 ? (
              <div
                className={
                  isCard
                    ? 'text-sm text-muted-foreground'
                    : 'rounded-lg border border-dashed px-3 py-4 text-xs text-muted-foreground'
                }
              >
                {resolvedEmptyMessage}
              </div>
            ) : (
              <div className="space-y-2">
                {items.map((item, index) => (
                  <div key={item.uid ?? `${name}.${index}`} className="space-y-1.5">
                    <div className={isCard ? 'flex gap-3' : 'flex gap-1.5'}>
                      <Field aria-invalid={hasError(`${name}.${index}.key`)}>
                        <FastField name={`${name}.${index}.key`}>
                          {({ field }: FormikFieldRenderProps) => (
                            <Input
                              {...field}
                              placeholder="Key"
                              className={isCard ? 'flex-1' : 'h-8 text-xs font-mono'}
                              aria-invalid={hasError(`${name}.${index}.key`)}
                            />
                          )}
                        </FastField>
                        <ErrorField name={`${name}.${index}.key`} />
                      </Field>

                      <Field aria-invalid={hasError(`${name}.${index}.value`)}>
                        <FastField name={`${name}.${index}.value`}>
                          {({ field }: FormikFieldRenderProps) => (
                            <Input
                              {...field}
                              placeholder="Value"
                              className={isCard ? 'flex-1' : 'h-8 text-xs font-mono'}
                              aria-invalid={hasError(`${name}.${index}.value`)}
                            />
                          )}
                        </FastField>
                        <ErrorField name={`${name}.${index}.value`} />
                      </Field>

                      <Button
                        variant={isCard ? 'outline' : 'ghost'}
                        size="icon"
                        type="button"
                        className={isCard ? undefined : 'h-8 w-8 shrink-0'}
                        onClick={() => arrayHelpers.remove(index)}
                      >
                        {isCard ? <Trash2 className="h-4 w-4" /> : <X className="h-3.5 w-3.5" />}
                      </Button>
                    </div>

                    {/* {isCard ? ( */}
                    {/*   <div className="grid gap-1.5 sm:grid-cols-2"> */}
                    {/*     <ErrorField name={`${name}.${index}.key`} /> */}
                    {/*     <ErrorField name={`${name}.${index}.value`} /> */}
                    {/*   </div> */}
                    {/* ) : ( */}
                    {/*   <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_2rem] gap-1.5"> */}
                    {/*     <ErrorField name={`${name}.${index}.key`} /> */}
                    {/*     <ErrorField name={`${name}.${index}.value`} /> */}
                    {/*     <div /> */}
                    {/*   </div> */}
                    {/* )} */}
                  </div>
                ))}
              </div>
            )}

            {isCard && (
              <Button
                variant="outline"
                size="sm"
                type="button"
                onClick={() => arrayHelpers.push({ key: '', value: '' })}
              >
                <Plus className="mr-2 h-4 w-4" />
                {addLabel}
              </Button>
            )}
          </div>
        )}
      />
    );

    if (!isCard) {
      return content;
    }

    return (
      <Card className="rounded-2xl border-0 bg-muted p-1 shadow-none gap-0">
        <CardHeader className="px-5 py-3">
          <CardTitle className="text-sm font-semibold">{title}</CardTitle>
          <p className="text-sm text-muted-foreground">{description}</p>
        </CardHeader>
        <CardContent className="rounded-xl bg-card p-5 shadow-sm">{content}</CardContent>
      </Card>
    );
  }
);
